<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

type TTestingMethod = 'all' | 'sast-dast' | 'sast' | 'dast';

type TTestingMethodCounts = {
  all: number;
  sastDast: number;
  sast: number;
  dast: number;
};

const props = withDefaults(
  defineProps<{
    initialValue: TTestingMethod;
    testingMethodCounts?: TTestingMethodCounts;
  }>(),
  {
    testingMethodCounts: () => ({ all: 0, sastDast: 0, sast: 0, dast: 0 }),
  }
);

const emit = defineEmits<{
  (e: 'filterChanged', filter: TTestingMethod): void;
}>();

const filterContainer = ref<HTMLElement | null>(null);
const all = ref<HTMLButtonElement | null>(null);
const sastDast = ref<HTMLButtonElement | null>(null);
const sast = ref<HTMLButtonElement | null>(null);
const dast = ref<HTMLButtonElement | null>(null);

const buttonRefs: Record<TTestingMethod, typeof all> = {
  all,
  'sast-dast': sastDast,
  sast,
  dast,
};

const activeFilter = ref<TTestingMethod>(props.initialValue);
const activeItemStyle = ref<Record<string, string | number>>({
  transform: 'translateX(0px)',
  width: '0px',
  opacity: 0,
});
let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

function setActiveItemStyle({ shouldScroll = true }: { shouldScroll?: boolean } = {}) {
  nextTick(() => {
    const activeButton = buttonRefs[activeFilter.value].value;
    if (!activeButton || !filterContainer.value) return;

    const containerRect = filterContainer.value.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const padding = parseFloat(window.getComputedStyle(filterContainer.value).paddingLeft) || 0;

    const offsetX =
      buttonRect.left - containerRect.left - padding + filterContainer.value.scrollLeft;

    activeItemStyle.value = {
      transform: `translateX(${offsetX}px)`,
      width: `${buttonRect.width}px`,
      height: `${buttonRect.height}px`,
    };

    if (window.innerWidth > 768 || !shouldScroll) return;

    setTimeout(() => {
      activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 300);
  });
}

function setActiveFilter(filter: TTestingMethod) {
  const countKey = filter === 'sast-dast' ? 'sastDast' : filter;
  if (props.testingMethodCounts[countKey] === 0) return;

  activeFilter.value = filter;
  setActiveItemStyle();
  emit('filterChanged', filter);
}

function handleResize() {
  if (resizeTimeout) clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => setActiveItemStyle({ shouldScroll: false }), 150);
}

watch(
  () => props.initialValue,
  (newVal) => {
    activeFilter.value = newVal;
    setActiveItemStyle();
  }
);

onMounted(async () => {
  await nextTick();

  requestAnimationFrame(() => {
    setActiveItemStyle({ shouldScroll: false });
  });
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  if (resizeTimeout) clearTimeout(resizeTimeout);
});
</script>

<template>
  <div ref="filterContainer" class="testing-methods-filter">
    <div class="active-background" :style="activeItemStyle"></div>
    <button
      ref="all"
      type="button"
      class="item"
      :class="{
        active: activeFilter === 'all',
        disabled: testingMethodCounts.all === 0,
      }"
      :disabled="testingMethodCounts.all === 0"
      @click="setActiveFilter('all')"
    >
      <div>All vulnerabilities</div>
      <div class="count">{{ testingMethodCounts.all }}</div>
    </button>
    <button
      ref="sastDast"
      type="button"
      class="item"
      :class="{
        active: activeFilter === 'sast-dast',
        disabled: testingMethodCounts.sastDast === 0,
      }"
      :disabled="testingMethodCounts.sastDast === 0"
      @click="setActiveFilter('sast-dast')"
    >
      <div>SAST + DAST</div>
      <div class="count">{{ testingMethodCounts.sastDast }}</div>
    </button>
    <button
      ref="sast"
      type="button"
      class="item"
      :class="{
        active: activeFilter === 'sast',
        disabled: testingMethodCounts.sast === 0,
      }"
      :disabled="testingMethodCounts.sast === 0"
      @click="setActiveFilter('sast')"
    >
      <div>SAST Only</div>
      <div class="count">{{ testingMethodCounts.sast }}</div>
    </button>
    <button
      ref="dast"
      type="button"
      class="item"
      :class="{
        active: activeFilter === 'dast',
        disabled: testingMethodCounts.dast === 0,
      }"
      :disabled="testingMethodCounts.dast === 0"
      @click="setActiveFilter('dast')"
    >
      <div>DAST Only</div>
      <div class="count">{{ testingMethodCounts.dast }}</div>
    </button>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/css/fonts';
@import '@/assets/css/variables';

.testing-methods-filter {
  position: relative;
  padding: 4px;
  display: inline-flex;
  background: #f8fafc;
  border-radius: 13px;

  @media screen and (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    max-width: calc(100% + 16px);
    margin-right: -16px;
  }

  .active-background {
    position: absolute;
    top: 4px;
    left: 4px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 12px 16px 0 rgba(178, 185, 195, 0.085);
    transition:
      transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.3s ease;
    pointer-events: none;
    z-index: 1;
  }

  .item {
    position: relative;
    padding: 10px 30px;
    display: flex;
    align-items: center;
    gap: 8px;
    @include Aspekta-font(400, 14px, 1.4, #5c6a7a);
    border-radius: 10px;
    border: none;
    background: none;
    transition: color 0.3s ease;
    cursor: pointer;
    white-space: nowrap;
    z-index: 2;

    @media screen and (min-width: 1024px) {
      &:hover:not(.disabled) {
        color: #021631;
      }
    }

    &.active {
      color: #021631;
      font-weight: 500;
    }

    &.disabled {
      color: #a3adb8;
      cursor: not-allowed;
      opacity: 0.5;

      &:hover {
        color: #a3adb8;
      }
    }
  }
}

.count {
  padding: 4px 8px;
  background: #ebf2fd;
  border-radius: 20px;
  color: #2b75f5;
}
</style>
